/**
 * Barra de búsqueda y filtros para la lista de formularios.
 * Permite buscar por texto y filtrar por tipo de vehículo/estado.
 */
import { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, SlidersHorizontal, Clock, Hash, ChevronsDownUp, ChevronsUpDown, BarChart3, ArrowUp01, ArrowDown01 } from 'lucide-react';
import styles from './SearchBar.module.css';

type NumeroFilter = 'par' | 'impar' | 'sin_numero';

/** Orden por n.º de vehículo (id); null = orden por defecto (fecha). */
export type SortById = 'asc' | 'desc' | null;

export interface SearchFilters {
    showOnlyPending: boolean;
    numeroFilter: NumeroFilter[];
    numeroMin: number | null;
    numeroMax: number | null;
    pageSize: number;
    sortById: SortById;
}

export const EMPTY_FILTERS: SearchFilters = {
    showOnlyPending: false,
    numeroFilter: [],
    numeroMin: null,
    numeroMax: null,
    pageSize: 20,
    sortById: null,
};

function formatPageSizeLabel(pageSize: number): string {
    if (pageSize === 0) return 'Mostrar todos los formularios';
    return `Mostrar ${pageSize} formularios`;
}

interface SearchBarProps {
    value: string;
    onChangeText: (text: string) => void;
    placeholder?: string;
    filters?: SearchFilters;
    onFiltersChange?: (filters: SearchFilters) => void;
    expandAll?: boolean;
    onExpandAllChange?: (value: boolean) => void;
    /** Abre el modal de totales (marcador); si no se pasa, no se muestra el botón. */
    onOpenMarker?: () => void;
}

export default function SearchBar({
    value,
    onChangeText,
    placeholder = "Buscar...",
    filters = EMPTY_FILTERS,
    onFiltersChange,
    expandAll = false,
    onExpandAllChange,
    onOpenMarker,
}: SearchBarProps) {
    const [showPanel, setShowPanel] = useState(false);

    const hasActiveFilters =
        filters.numeroFilter.length > 0 ||
        filters.numeroMin !== null ||
        filters.numeroMax !== null ||
        filters.pageSize !== EMPTY_FILTERS.pageSize ||
        filters.sortById !== null;

    const validRange = (filters.numeroMin === null || filters.numeroMax === null) ||
        (filters.numeroMin <= filters.numeroMax);

    const closePanel = () => setShowPanel(false);
    const removeFilter = (key: string, chipValue?: string) => {
        if (!onFiltersChange) return;
        const updated = { ...filters };
        if (key === 'pending') updated.showOnlyPending = false;
        else if (key === 'numero' && chipValue) updated.numeroFilter = filters.numeroFilter.filter(f => f !== chipValue);
        else if (key === 'range') { updated.numeroMin = null; updated.numeroMax = null; }
        else if (key === 'pageSize') { updated.pageSize = EMPTY_FILTERS.pageSize; }
        else if (key === 'sortById') { updated.sortById = null; }
        onFiltersChange(updated);
    };

    const toggleNumeroFilter = (f: NumeroFilter) => {
        if (!onFiltersChange) return;
        const current = filters.numeroFilter;
        let next: NumeroFilter[];
        if (current.includes(f)) {
            next = current.filter(x => x !== f);
        } else if ((f === 'par' || f === 'impar') && current.includes(f === 'par' ? 'impar' : 'par')) {
            next = current.filter(x => x !== (f === 'par' ? 'impar' : 'par')).concat(f);
        } else {
            next = [...current, f];
        }
        onFiltersChange({ ...filters, numeroFilter: next });
    };

    const toggleSortById = (dir: 'asc' | 'desc') => {
        if (!onFiltersChange) return;
        onFiltersChange({ ...filters, sortById: filters.sortById === dir ? null : dir });
    };

    const handleRangeChange = (type: 'min' | 'max', text: string) => {
        if (!onFiltersChange) return;
        if (text === '') {
            onFiltersChange({ ...filters, [type === 'min' ? 'numeroMin' : 'numeroMax']: null });
            return;
        }
        const num = parseInt(text);
        if (isNaN(num) || num < 0) return;
        onFiltersChange({ ...filters, [type === 'min' ? 'numeroMin' : 'numeroMax']: num });
    };

    const handlePageSize = (size: number) => {
        if (!onFiltersChange) return;
        onFiltersChange({ ...filters, pageSize: size });
    };

    const activeChips = useMemo(() => {
        const chips: { key: string; value?: string; label: string }[] = [];
        if (filters.showOnlyPending) chips.push({ key: 'pending', label: 'Reservas pendientes' });
        filters.numeroFilter.forEach(f => {
            const label = f === 'par' ? 'Números pares' : f === 'impar' ? 'Números impares' : 'Sin id';
            chips.push({ key: 'numero', value: f, label });
        });
        if (filters.numeroMin !== null || filters.numeroMax !== null) {
            const label = filters.numeroMin !== null && filters.numeroMax !== null
                ? `Rango: ${filters.numeroMin} – ${filters.numeroMax}`
                : filters.numeroMin !== null ? `Desde: ${filters.numeroMin}` : `Hasta: ${filters.numeroMax}`;
            chips.push({ key: 'range', label });
        }
        if (filters.pageSize !== EMPTY_FILTERS.pageSize) {
            chips.push({ key: 'pageSize', label: formatPageSizeLabel(filters.pageSize) });
        }
        if (filters.sortById === 'asc') {
            chips.push({ key: 'sortById', label: 'ID ↑ ascendente' });
        } else if (filters.sortById === 'desc') {
            chips.push({ key: 'sortById', label: 'ID ↓ descendente' });
        }
        return chips;
    }, [filters]);

    return (
        <div className={styles.container}>
            <div className={styles.searchSection}>
                {onOpenMarker ? (
                    <div className={styles.leftIcons}>
                        <button
                            type="button"
                            className={styles.markerBtn}
                            onClick={onOpenMarker}
                            aria-label="Abrir resumen de totales"
                            title="Totales"
                        >
                            <BarChart3 size={18} color="currentColor" strokeWidth={2.25} />
                        </button>
                    </div>
                ) : null}
                <input className={styles.input} placeholder={placeholder} value={value} onChange={e => onChangeText(e.target.value)} />
                <div className={styles.rightIcons}>
                    {onFiltersChange && (
                        <button
                            type="button"
                            className={`${styles.pendingBtn} ${filters.showOnlyPending ? styles.pendingBtnActive : ''}`}
                            onClick={() => onFiltersChange({ ...filters, showOnlyPending: !filters.showOnlyPending })}
                            aria-label={filters.showOnlyPending ? 'Quitar filtro: reservas pendientes' : 'Filtrar: solo reservas pendientes (falta algún check)'}
                            aria-pressed={filters.showOnlyPending}
                            title={filters.showOnlyPending ? 'Mostrar todas las reservas' : 'Solo reservas pendientes — falta documentación o billetes'}
                        >
                            <Clock size={18} color="currentColor" />
                        </button>
                    )}
                    {onExpandAllChange && (
                        <button
                            type="button"
                            className={`${styles.expandAllBtn} ${expandAll ? styles.expandAllBtnActive : ''}`}
                            onClick={() => onExpandAllChange(!expandAll)}
                            aria-pressed={expandAll}
                            aria-label={expandAll ? 'Desplegar todas las fichas' : 'Plegar todas las fichas'}
                            title={expandAll ? 'Desplegar todos los formularios' : 'Plegar todos los formularios'}
                        >
                            {expandAll ? <ChevronsUpDown size={18} color="currentColor" strokeWidth={2} /> : <ChevronsDownUp size={18} color="currentColor" strokeWidth={2} />}
                        </button>
                    )}
                    {onFiltersChange && (
                        <button
                            type="button"
                            className={`${styles.filterBtn} ${hasActiveFilters ? styles.filterBtnActive : ''}`}
                            onClick={() => setShowPanel(true)}
                            aria-label="Filtros avanzados"
                            title="Filtros"
                        >
                            <SlidersHorizontal size={18} color="currentColor" />
                        </button>
                    )}
                </div>
            </div>

            {activeChips.length > 0 && (
                <div className={styles.chipsRow}>
                    {activeChips.map((chip, i) => (
                        <div key={`${chip.key}-${chip.value ?? i}`} className={styles.chip}>
                            <span className={styles.chipText}>{chip.label}</span>
                            <button className={styles.chipX} onClick={() => removeFilter(chip.key, chip.value)}>
                                <X size={12} color="#fff" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {showPanel && createPortal(
                <div className={styles.overlay} onClick={closePanel} role="dialog" aria-modal="true" aria-label="Filtros avanzados">
                    <div className={styles.panel} onClick={e => e.stopPropagation()}>
                        <div className={styles.panelHeader}>
                            <span className={styles.panelTitle}>Filtros</span>
                            <button
                                type="button"
                                className={styles.panelCloseBtn}
                                onClick={closePanel}
                                aria-label="Cerrar filtros"
                            >
                                <X size={18} color="currentColor" strokeWidth={2.25} />
                            </button>
                        </div>

                        <div className={styles.panelSections}>
                            <section className={styles.filterSection}>
                                <span className={styles.sectionLabel}>Números de vehículo</span>
                                <div className={styles.optionStack}>
                                    {([
                                        { value: 'par' as NumeroFilter, label: 'Números pares' },
                                        { value: 'impar' as NumeroFilter, label: 'Números impares' },
                                        { value: 'sin_numero' as NumeroFilter, label: 'Sin id' },
                                    ]).map(opt => {
                                        const active = filters.numeroFilter.includes(opt.value);
                                        return (
                                            <button key={opt.value} type="button" className={`${styles.optionBtn} ${active ? styles.optionBtnActive : ''}`} onClick={() => toggleNumeroFilter(opt.value)}>
                                                <Hash size={16} color="currentColor" strokeWidth={2} />
                                                <span className={styles.optionBtnLabel}>{opt.label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </section>

                            <section className={styles.filterSection}>
                                <span className={`${styles.sectionLabel} ${!validRange ? styles.sectionLabelError : ''}`}>Rango de números</span>
                                <div className={styles.rangeRow}>
                                    <div className={styles.rangeField}>
                                        <span className={styles.rangeLabel}>Desde:</span>
                                        <input
                                            className={`${styles.rangeInput} ${!validRange ? styles.rangeInputError : ''}`}
                                            inputMode="numeric"
                                            placeholder="Ej: 1"
                                            value={filters.numeroMin?.toString() ?? ''}
                                            onChange={e => handleRangeChange('min', e.target.value)}
                                        />
                                    </div>
                                    <div className={styles.rangeField}>
                                        <span className={styles.rangeLabel}>Hasta:</span>
                                        <input
                                            className={`${styles.rangeInput} ${!validRange ? styles.rangeInputError : ''}`}
                                            inputMode="numeric"
                                            placeholder="Ej: 100"
                                            value={filters.numeroMax?.toString() ?? ''}
                                            onChange={e => handleRangeChange('max', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </section>

                            <section className={styles.filterSection}>
                                <span className={styles.sectionLabel}>Ordenar por ID</span>
                                <div className={styles.sortIdRow}>
                                    <button
                                        type="button"
                                        className={`${styles.sortIdBtn} ${filters.sortById === 'asc' ? styles.sortIdBtnActive : ''}`}
                                        onClick={() => toggleSortById('asc')}
                                        aria-pressed={filters.sortById === 'asc'}
                                    >
                                        <ArrowUp01 size={18} color="currentColor" strokeWidth={2} aria-hidden />
                                        <span className={styles.sortIdBtnTitle}>Ascendente</span>
                                    </button>
                                    <button
                                        type="button"
                                        className={`${styles.sortIdBtn} ${filters.sortById === 'desc' ? styles.sortIdBtnActive : ''}`}
                                        onClick={() => toggleSortById('desc')}
                                        aria-pressed={filters.sortById === 'desc'}
                                    >
                                        <ArrowDown01 size={18} color="currentColor" strokeWidth={2} aria-hidden />
                                        <span className={styles.sortIdBtnTitle}>Descendente</span>
                                    </button>
                                </div>
                            </section>

                            <section className={styles.filterSection}>
                                <span className={styles.sectionLabel}>{formatPageSizeLabel(filters.pageSize)}</span>
                                <div className={styles.pageSizeRow}>
                                    {[10, 20, 50, 100].map(size => (
                                        <button key={size} type="button" className={`${styles.pageSizeBtn} ${filters.pageSize === size ? styles.pageSizeBtnActive : ''}`} onClick={() => handlePageSize(size)}>
                                            {size}
                                        </button>
                                    ))}
                                    <button type="button" className={`${styles.pageSizeBtn} ${filters.pageSize === 0 ? styles.pageSizeBtnActive : ''}`} onClick={() => handlePageSize(0)}>
                                        Todos
                                    </button>
                                </div>
                            </section>
                        </div>
                    </div>
                </div>,
                document.body,
            )}
        </div>
    );
}
